import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { DiktatFeld, VorschlagsKasten } from '@symphenia/ui-components'
import { regieZeileAusDiktat, type RegieInhalt, type SiteDocAiProposal, type SiteDocAiProposalItem, type SiteDocDetail, type SiteDocDiktatZeile } from '@symphenia/shared-types'
import { fehlertext } from '@/lib/fehlertext'
import { useFeld, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { regieLineCreate, siteDocAiProposal, updateSiteDoc } from '../../api/site-docs.api'

type Eintrag = SiteDocAiProposalItem<SiteDocDiktatZeile | { kopf: string; anhaengen: boolean }>

/** Online-Zustand des Browsers — der Knopf braucht Netz (AP-46 21.1). */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const an = () => setOnline(true)
    const aus = () => setOnline(false)
    window.addEventListener('online', an)
    window.addEventListener('offline', aus)
    return () => {
      window.removeEventListener('online', an)
      window.removeEventListener('offline', aus)
    }
  }, [])
  return online
}

/**
 * AP-27 18. im ERP — **Diktat in Zeilen** am Entwurf des Regieberichts: Feld „Was wurde gemacht?" (gespeichert
 * als `dictation` am Entwurf, beim Abschluss geleert), „Vorschlag aus dem Text" und der Vorschlagskasten.
 * Übernehmen legt die Zeilen mit `origin = DICTATION` hinter den vorhandenen an; die Beschreibung nur, wenn sie
 * leer ist — sonst als „Text anhängen".
 */
export function RegieDiktat({ dok, inhalt, onGeaendert }: { dok: SiteDocDetail; inhalt: RegieInhalt; onGeaendert: (neu: SiteDocDetail) => void }) {
  const { t } = useTranslation()
  const online = useOnline()
  const daten = useMemo<Datensatz>(() => ({ dictation: dok.dictation ?? '' }), [dok.dictation])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `regieDiktat:${dok.id}`,
    bezeichnung: t('siteDocs.diktat.titel'),
    daten,
    schreibe: async (patch) => {
      const neu = await updateSiteDoc(dok.kind, dok.id, { dictation: (patch.dictation as string | null) || null })
      onGeaendert(neu)
      return { dictation: neu.dictation ?? '' }
    },
  }) as Speicherstelle<unknown>
  const feld = useFeld<string | null>(stelle, 'dictation', { label: t('siteDocs.diktat.titel') })
  const [vorschlag, setVorschlag] = useState<SiteDocAiProposal | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const eintraege = useMemo<Eintrag[]>(() => {
    if (!vorschlag) return []
    const zeilen = vorschlag.items as Eintrag[]
    const kopf = typeof vorschlag.kopf?.beschreibung === 'string' ? vorschlag.kopf.beschreibung : null
    if (!kopf) return zeilen
    return [{ key: 'kopf', daten: { kopf, anhaengen: !!inhalt.beschreibung.trim() }, vorgewaehlt: !inhalt.beschreibung.trim(), hinweise: [] }, ...zeilen]
  }, [vorschlag, inhalt.beschreibung])

  const holen = async () => {
    setFehler(null)
    try {
      setVorschlag(await siteDocAiProposal({ kind: 'REGIE', text: feld.props.value, projectId: dok.projectId, projectRefId: dok.projectRefId, documentDate: dok.documentDate.slice(0, 10) }))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  const uebernehmen = async (liste: Eintrag[]) => {
    setFehler(null)
    let letzt: SiteDocDetail | null = null
    let reihe = inhalt.zeilen.reduce((m, z) => Math.max(m, z.sortOrder), -1) + 1
    try {
      for (const e of liste) {
        if ('kopf' in e.daten) {
          const neu = e.daten.anhaengen ? `${inhalt.beschreibung.trim()}\n${e.daten.kopf}` : e.daten.kopf
          letzt = await updateSiteDoc(dok.kind, dok.id, { content: { beschreibung: neu, anordnung: inhalt.anordnung ?? null } })
          continue
        }
        const z = regieZeileAusDiktat(e.daten, crypto.randomUUID(), reihe++)
        letzt = await regieLineCreate(dok.id, {
          kind: z.kind,
          description: z.description,
          quantity: Number(z.quantity),
          unitCode: z.unitCode,
          ...(z.personName ? { personName: z.personName } : {}),
          ...(z.personUserId ? { personUserId: z.personUserId } : {}),
          ...(z.fromTime ? { fromTime: z.fromTime } : {}),
          ...(z.toTime ? { toTime: z.toTime } : {}),
          ...(z.articleId ? { articleId: z.articleId } : {}),
          ...(z.articleNumber ? { articleNumber: z.articleNumber } : {}),
          ...(z.articleName ? { articleName: z.articleName } : {}),
          ...(z.unitId ? { unitId: z.unitId } : {}),
          ...(z.surchargeKey ? { surchargeKey: z.surchargeKey } : {}),
          sortOrder: z.sortOrder,
          origin: 'DICTATION',
        })
      }
    } catch (e) {
      setFehler(fehlertext(e, t))
      throw e
    } finally {
      if (letzt) onGeaendert(letzt)
    }
  }

  const darstellung = (e: Eintrag) => {
    if ('kopf' in e.daten) return <span>{t(e.daten.anhaengen ? 'siteDocs.diktat.anhaengen' : 'siteDocs.diktat.beschreibung', { text: e.daten.kopf })}</span>
    const d = e.daten
    const menge = `${String(d.quantity).replace('.', ',')} ${d.unitCode}`.trim()
    return (
      <span>
        <span className="font-medium">{t(`siteDocs.regie.groups.${d.kind}`)}:</span> {d.kind === 'SERVICE' ? [d.personName, d.description].filter(Boolean).join(' · ') : d.articleName ?? d.description}
        <span className="ml-2 text-muted-foreground">{[d.fromTime && d.toTime ? `${d.fromTime}–${d.toTime}` : null, menge, d.surchargeLabel].filter(Boolean).join(' · ')}</span>
      </span>
    )
  }

  return (
    <div className="space-y-3 rounded-md border border-border p-3">
      <DiktatFeld
        wert={feld.props.value}
        onChange={(v) => feld.props.onChange({ target: { value: v } } as ChangeEvent<HTMLTextAreaElement>)}
        onBlur={feld.props.onBlur}
        onVorschlag={holen}
        online={online}
        fehler={fehler}
        texte={{ titel: t('siteDocs.diktat.titel'), tastatur: t('siteDocs.diktat.tastatur'), knopf: t('siteDocs.diktat.knopf'), arbeitet: t('siteDocs.diktat.arbeitet'), ohneNetz: t('siteDocs.diktat.ohneNetz'), kiHinweis: t('siteDocs.diktat.kiHinweis') }}
      />
      {vorschlag && (
        <>
          {!!vorschlag.hinweise?.length && (
            <ul className="list-disc space-y-0.5 pl-5 text-sm text-amber-800 dark:text-amber-200">
              {vorschlag.hinweise.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          )}
          <VorschlagsKasten
            eintraege={eintraege}
            darstellung={darstellung}
            onUebernehmen={uebernehmen}
            onVerwerfen={() => setVorschlag(null)}
            texte={{
              titel: t('siteDocs.diktat.vorschlag'),
              uebernehmen: t('siteDocs.diktat.uebernehmen'),
              markierte: t('siteDocs.diktat.markierte'),
              verwerfen: t('siteDocs.diktat.verwerfen'),
              leer: t('siteDocs.diktat.leer'),
              hinweis: (c) => t(`siteDocs.diktat.hints.${c}`, c),
            }}
          />
        </>
      )}
    </div>
  )
}
